import React from "react";
import { useDeleteHeadroomProjectMutation } from "graphql/types/graphql";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { enqueueSnackbar } from "notistack";
import { useHeadroomContext } from "../context/HeadRoomProvider";

export const useDeleteHeadroomProject = () => {
  const { setSelectedProject } = useHeadroomContext();
  const { showError, clearError } = useErrorSnackbar();
  const [deleteHeadroomProject, { loading: deleteLoading, error: deleteError }] = useDeleteHeadroomProjectMutation();

  React.useEffect(() => {
    if (deleteError) {
      showError(deleteError.message || "Failed to delete project!");
    } else {
      clearError();
    }
  }, [deleteError, showError, clearError]);

  const deleteProject = async (projectName: string, refetchProjects?: () => void) => {
    try {
      const { data, errors } = await deleteHeadroomProject({
        variables: { projectName },
        update: (cache) => {
          cache.evict({ id: "ROOT_QUERY", fieldName: "getAllHeadroomProjects" });
        },
      });

      if (errors && errors.length > 0) {
        enqueueSnackbar(errors[0].message, { variant: "error" });
        return { success: false, errorMessage: errors[0].message };
      }

      if (data?.deleteHeadroomProject?.success) {
        enqueueSnackbar("Project deleted successfully!", { variant: "success" });
        setSelectedProject(null);

        return data.deleteHeadroomProject;
      } else {
        const errorMessage = data?.deleteHeadroomProject?.errorMessage || "Failed to delete project";
        showError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
        return { success: false, errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
      return { success: false, errorMessage };
    }
  };

  return {
    deleteProject,
    loading: deleteLoading,
  };
};
